/// <amd-module name="Wit/Common/KanbanBoardSettings/Components/BoardSettingsViews/ColumnSettingsView" />
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { IReadonlyObservableValue, useDerivedObservable, useObservable, useSubscription } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { Button } from "VSSUI/Button";
import { Checkbox } from "VSSUI/Checkbox";
import { Dropdown } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { Header, TitleSize } from "VSSUI/Header";
import { IMenuButtonProps, IMenuItem, MenuButton } from "VSSUI/Menu";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Tab, TabBar } from "VSSUI/Tabs";
import { TextField } from "VSSUI/TextField";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { BoardColumn, BoardColumnType } from "Wit/BoardsHub/Common/Client/Contracts/Settings";
import { IColumnsSettings } from "Wit/Common/KanbanBoardSettings/Components/BoardSettingsViews/ColumnSettings";
import * as Resources from "Wit/Common/KanbanBoardSettings/Resources";
import { ISettingsView } from "Wit/Common/WitSettingsPanelTypes/Components/WitSettingsPanel.props";
import { HorizontalScrollButtons } from "./HorizontalScrollButtons";

export function getColumnsSettingsTab(
    columnSettings: IColumnsSettings,
    disabled: boolean,
    groupId: string,
    onSaveSettings?: () => Promise<void>
): ISettingsView {
    return {
        groupId: groupId,
        isDirty: columnSettings.dirtyTracker.isDirty,
        isValid: columnSettings.validator.isValid,
        name: "Columns",
        onSaveSettings,
        renderContent: () => <ColumnsSettingsView columnsSettings={columnSettings} disabled={disabled} />
    };
}

interface IColumnSettingsViewProps {
    columnsSettings: IColumnsSettings;
    disabled?: boolean;
}

function ColumnsSettingsView({ columnsSettings, disabled }: IColumnSettingsViewProps) {
    const firstTabId = columnsSettings.columns.value[0].id;
    const [selectedTabId, setSelectedTabId] = useObservable(firstTabId);

    useEffect(() => {
        const ensureSelectedTabExists = () => {
            // If the selected column is not in the list of columns (e.g. if the
            // selected tab has been removed), select the first column.
            if (!columnsSettings.columns.value.map(c => c.id).includes(selectedTabId.value)) {
                setSelectedTabId(columnsSettings.columns.value[0].id);
            }
        };

        columnsSettings.columns.subscribe(ensureSelectedTabExists);
        selectedTabId.subscribe(ensureSelectedTabExists);

        return () => {
            columnsSettings.columns.unsubscribe(ensureSelectedTabExists);
            selectedTabId.unsubscribe(ensureSelectedTabExists);
        };
    }, [columnsSettings.columns, selectedTabId]);

    return (
        <div className="column-settings-view">
            <Header title={Resources.ColumnsSettingsPanelTitle} titleSize={TitleSize.Small} description={Resources.ColumnsSettingsPanelSubtitle} />

            {!disabled && (
                <div className="margin-horizontal-20">
                    <Button
                        subtle
                        text={Resources.AddColumnButtonText}
                        iconProps={{ iconName: "Add" }}
                        onClick={() => {
                            setSelectedTabId(columnsSettings.addColumn());
                            // TODO: highlight the name input
                        }}
                    />
                </div>
            )}

            <Observer boardColumns={columnsSettings.columns} selectedId={selectedTabId}>
                {({ boardColumns, selectedId }: { boardColumns: readonly BoardColumn[]; selectedId: string }) => (
                    <ColumnsSettingsTabs
                        boardColumns={boardColumns}
                        disabled={disabled}
                        selectedTabId={selectedId}
                        onTabSelected={setSelectedTabId}
                        columnsSettings={columnsSettings}
                    />
                )}
            </Observer>

            <Observer selectedTabId={selectedTabId}>
                {({ selectedTabId }: { selectedTabId: string }) => (
                    <ColumnSettingsTabArea disabled={disabled} selectedBoardColumnId={selectedTabId} columnsSettings={columnsSettings} />
                )}
            </Observer>
        </div>
    );
}

interface IColumnsSettingsTabsProps {
    boardColumns: readonly BoardColumn[];
    disabled?: boolean;
    selectedTabId: string;
    onTabSelected: (tabId: string) => void;
    columnsSettings: IColumnsSettings;
}

function ColumnsSettingsTabs({ boardColumns, disabled, selectedTabId, onTabSelected, columnsSettings }: IColumnsSettingsTabsProps) {
    const tabCount = boardColumns.length;

    // This nonsense is because the TabBar needs its Tabs to be direct children, which means we can't have an observable around each tab.
    const observables: { [columnId: string]: IReadonlyObservableValue<boolean> } = {};
    for (const boardColumn of boardColumns) {
        observables[boardColumn.id] = columnsSettings.validator.isColumnValid(boardColumn.id);
    }

    return (
        <Observer {...observables}>
            {() => (
                <HorizontalScrollButtons>
                    <TabBar selectedTabId={selectedTabId} onSelectedTabChanged={onTabSelected}>
                        {boardColumns.map(boardColumn => (
                            <Tab
                                className="columns-settings-tab"
                                key={`column-settings-tab-${boardColumn.id}`}
                                id={boardColumn.id}
                                name={boardColumn.name}
                                iconProps={
                                    observables[boardColumn.id].value ? undefined : { iconName: "AlertSolid", className: "error-text icon-margin" }
                                }
                                renderBadge={
                                    !disabled
                                        ? () => (
                                              <MenuButton
                                                  ariaLabel={Resources.ColumnOptionsMenuLabel}
                                                  className="columns-settings-tab-menu-button margin-left-8"
                                                  {...makeMenuProps(boardColumn, tabCount, columnsSettings)}
                                              />
                                          )
                                        : undefined
                                }
                            />
                        ))}
                    </TabBar>
                </HorizontalScrollButtons>
            )}
        </Observer>
    );
}

function makeMenuProps(boardcolumn: BoardColumn, tabCount: number, columnsSettings: IColumnsSettings): IMenuButtonProps {
    const menuItems: IMenuItem[] = [];

    if (boardcolumn.order !== 0 && boardcolumn.order !== tabCount - 1) {
        menuItems.push({
            id: `remove-boardcolumn-${boardcolumn.id}`,
            text: Resources.ColumnContextMenuRemove,
            iconProps: { iconName: "Cancel" },
            onActivate: () => columnsSettings.removeColumn(boardcolumn.id)
        });
    }

    if (boardcolumn.order > 1 && boardcolumn.order !== tabCount - 1) {
        menuItems.push({
            id: `move-left-${boardcolumn.id}`,
            text: Resources.ColumnContextMenuMoveLeft,
            iconProps: { iconName: "ChevronLeft" },
            onActivate: () => columnsSettings.setOrder(boardcolumn.id, boardcolumn.order - 1)
        });
    }

    if (boardcolumn.order !== 0 && boardcolumn.order < tabCount - 2) {
        menuItems.push({
            id: `move-right-${boardcolumn.id}`,
            text: Resources.ColumnContextMenuMoveRight,
            iconProps: { iconName: "ChevronRight" },
            onActivate: () => columnsSettings.setOrder(boardcolumn.id, boardcolumn.order + 1)
        });
    }

    if (boardcolumn.order > 0) {
        menuItems.push({
            id: `insert-left-${boardcolumn.id}`,
            text: Resources.ColumnContextMenuInsertLeft,
            onActivate: () => {
                columnsSettings.addColumn(boardcolumn.order);
            }
        });
    }

    if (boardcolumn.order < tabCount - 1) {
        menuItems.push({
            id: `insert-right-${boardcolumn.id}`,
            text: Resources.ColumnContextMenuInsertRight,
            onActivate: () => {
                columnsSettings.addColumn(boardcolumn.order + 1);
            }
        });
    }

    return {
        hideDropdownIcon: true,
        subtle: true,
        iconProps: { iconName: "MoreVertical" },
        contextualMenuProps: {
            menuProps: {
                id: `columns-settings-boardcolumn-menu-${boardcolumn.id}`,
                items: menuItems
            }
        }
    };
}

interface IColumnSettingsTabAreaProps {
    disabled?: boolean;
    selectedBoardColumnId: string;
    columnsSettings: IColumnsSettings;
}

function ColumnSettingsTabArea({ selectedBoardColumnId, columnsSettings, disabled }: IColumnSettingsTabAreaProps) {
    const observableColumn = columnsSettings.getColumn(selectedBoardColumnId);

    if (!observableColumn) {
        return null;
    }

    return (
        <Observer boardColumn={observableColumn}>
            {({ boardColumn }: { boardColumn: BoardColumn }) => (
                <ColumnForm boardColumn={boardColumn} columnsSettings={columnsSettings} disabled={disabled} />
            )}
        </Observer>
    );
}

interface IColumnSettingsFormProps {
    boardColumn: BoardColumn;
    disabled?: boolean;
    columnsSettings: IColumnsSettings;
}

function ColumnForm(props: IColumnSettingsFormProps) {
    return (
        <>
            <BoardColumnName {...props} />
            <WorkInProgressLimit {...props} />
            <SplitColumn {...props} />
            <StateMapping {...props} />
            <DefinitionOfDone {...props} />
        </>
    );
}

function BoardColumnName({ boardColumn, columnsSettings, disabled }: IColumnSettingsFormProps) {
    const errorMessage = columnsSettings.validator.getNameError(boardColumn.id);
    const hasError = useDerivedObservable(errorMessage, em => !!em, [boardColumn.id]);

    return (
        <>
            <Header title={Resources.ColumnNameHeader} titleSize={TitleSize.Small} />

            <FormItem className="margin-horizontal-20" label={Resources.ColumnNameLabel} error={hasError} message={errorMessage}>
                <TextField disabled={disabled} value={boardColumn.name} onChange={(e, val) => columnsSettings.setName(boardColumn.id, val)} />
            </FormItem>
        </>
    );
}

function WorkInProgressLimit({ boardColumn, columnsSettings, disabled }: IColumnSettingsFormProps) {
    const errorMessage = columnsSettings.validator.getWIPLimitError(boardColumn.id);
    const hasError = useDerivedObservable(errorMessage, em => !!em, [boardColumn.id]);

    if (boardColumn.columnType !== BoardColumnType.InProgress) {
        return null;
    }

    return (
        <>
            <Header title={Resources.WorkInProgressLimitHeader} titleSize={TitleSize.Small} description={Resources.WorkInProgressLimitSubheader} />

            <FormItem className="margin-horizontal-20" label={Resources.WorkInProgressLimitLabel} error={hasError} message={errorMessage}>
                <TextField
                    disabled={disabled}
                    inputType="number"
                    value={"" + boardColumn.itemLimit}
                    onChange={(e, val) => columnsSettings.setWorkInProgressLimit(boardColumn.id, parseInt(val) ?? 0)}
                />
            </FormItem>
        </>
    );
}

function SplitColumn({ boardColumn, columnsSettings, disabled }: IColumnSettingsFormProps) {
    if (boardColumn.columnType !== BoardColumnType.InProgress) {
        return null;
    }

    return (
        <div className="margin-horizontal-20">
            <Checkbox
                className="header-label"
                disabled={disabled}
                onChange={(event, checked) => columnsSettings.setSplitColumn(boardColumn.id, checked)}
                checked={boardColumn.isSplit}
                label={Resources.SplitColumnCheckboxLabel}
            />
        </div>
    );
}

function StateMapping({ boardColumn, columnsSettings, disabled }: IColumnSettingsFormProps) {
    const description =
        boardColumn.columnType === BoardColumnType.Incoming
            ? Resources.StateMappingIncomingSubheader
            : boardColumn.columnType === BoardColumnType.Outgoing
            ? Resources.StateMappingOutgoingSubheader
            : undefined;

    return (
        <>
            <Header title={Resources.StateMappingHeader} titleSize={TitleSize.Small} description={description} />

            {/* Loop through dictionary of allowed state mappings instead of current state mapping because current mapping */}
            {/* might not include work item types that aren't assigned a state yet when board is in invalid state that needs to be fixed */}
            {Object.entries(columnsSettings.getAllowedStateMappings(boardColumn.id)).map(([workItemTypeName, allowedStates]) => {
                return (
                    <StateMappingItem
                        allowedStateMappings={allowedStates}
                        disabled={disabled}
                        key={`state-mapping-${boardColumn.id}-${workItemTypeName}`}
                        workItemTypeName={workItemTypeName}
                        mappedState={boardColumn.stateMappings[workItemTypeName]}
                        columnsSettings={columnsSettings}
                        boardColumnId={boardColumn.id}
                    />
                );
            })}
        </>
    );
}

interface IStateMappingItemProps {
    allowedStateMappings: string[];
    disabled?: boolean;
    workItemTypeName: string;
    mappedState: string | undefined;
    columnsSettings: IColumnsSettings;
    boardColumnId: string;
}

function StateMappingItem({ allowedStateMappings, workItemTypeName, mappedState, columnsSettings, boardColumnId, disabled }: IStateMappingItemProps) {
    const warning = columnsSettings.dirtyTracker.getStateMappingDirtyWarning(boardColumnId, workItemTypeName);
    const errorMessage = columnsSettings.validator.getStateMappingError(boardColumnId, workItemTypeName);
    const hasError = useDerivedObservable(errorMessage, em => !!em, [boardColumnId, workItemTypeName]);

    const [disabledState, setDisabledState] = useState(allowedStateMappings.length <= 1 && !hasError.value);
    useSubscription(hasError, error => setDisabledState(allowedStateMappings.length <= 1 && !error), [allowedStateMappings]);

    const selection = useRef(new DropdownSelection());
    const dropdownItems = allowedStateMappings.map(s => ({ id: s, text: s })) ?? [{ id: mappedState, text: mappedState }];

    useEffect(() => {
        selection.current.select(dropdownItems.findIndex(item => item.id === mappedState));
    }, [mappedState]);

    return (
        <>
            <FormItem
                className="margin-horizontal-20 margin-bottom-8"
                label={format(Resources.StateMappingWorkItemTypeFormat, workItemTypeName)}
                error={hasError}
                message={errorMessage}
            >
                <Dropdown
                    disabled={disabled || disabledState}
                    selection={selection.current}
                    items={dropdownItems}
                    onSelect={(e, item) => columnsSettings.setStateMapping(boardColumnId, workItemTypeName, item.id)}
                />
            </FormItem>
            <Observer warning={warning}>
                {() =>
                    warning.value ? (
                        <MessageCard className="margin-horizontal-20" severity={MessageCardSeverity.Warning}>
                            {warning.value}
                        </MessageCard>
                    ) : null
                }
            </Observer>
        </>
    );
}

function DefinitionOfDone({ boardColumn, columnsSettings, disabled }: IColumnSettingsFormProps) {
    const errorMessage = columnsSettings.validator.getDefinitionOfDoneError(boardColumn.id);
    const hasError = useDerivedObservable(errorMessage, em => !!em, [boardColumn.id]);

    if (boardColumn.columnType !== BoardColumnType.InProgress) {
        return null;
    }

    return (
        <FormItem
            className="header-form-item margin-horizontal-20 margin-top-16"
            label={Resources.DefinitionOfDoneLabel}
            error={hasError}
            message={errorMessage}
        >
            <TextField
                disabled={disabled}
                multiline
                value={boardColumn.description}
                onChange={(e, val) => columnsSettings.setDescription(boardColumn.id, val || null)}
            />
        </FormItem>
    );
}
